import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Search, X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  searchFilings,
  fetchFilingDetail,
  updateSearchParam,
  clearSearchParams,
} from "./reducers/patientFrameSlice";
import LabFilingDetails from "./LabFilingDetails";

const PathologySearch = () => {
  const dispatch = useDispatch();
  const {
    searchParams,
    searchResults,
    selectedFiling,
    loadingSearch,
    loadingDetail,
    searchError,
  } = useSelector((state) => state.s1PathologySearch);

  const license_key = useSelector(
    (state) => state.authen.loggedInUser?.license_key,
  );

  const [openDialog, setOpenDialog] = useState(false);

  const columns = [
    {
      field: "emis_id",
      headerName: "EMIS ID",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "lab_report_id",
      headerName: "Lab Report Id",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "category",
      headerName: "Category",
      flex: 2,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "category_type",
      headerName: "Report Type",
      flex: 2,
      align: "center",
      headerAlign: "center",
      renderCell: (params) =>
        params.value ? "Normal Report" : "Abnormal Report",
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "starttime",
      headerName: "Start Time",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "notes",
      headerName: "Notes",
      flex: 4,
      align: "center",
      headerAlign: "center",
    },
  ];

  const handleInputChange = (field, value) => {
    dispatch(updateSearchParam({ field, value }));
  };

  const handleSearch = () => {
    if (license_key) {
      dispatch(searchFilings({ searchParams, license_key }));
    }
  };

  const handleClearSearch = () => {
    dispatch(clearSearchParams());
  };

  const handleRowClick = (params) => {
    const lab_report_id = params.row?.lab_report_id;
    const liverun = searchParams.liverun;

    if (lab_report_id && license_key) {
      dispatch(fetchFilingDetail({ lab_report_id, liverun, license_key }));
      setOpenDialog(true);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography
          variant="h5"
          sx={{ mb: 3, fontWeight: "bold", color: "primary.main" }}
        >
          Patient Summary
        </Typography>

        {/* Row 1 */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
          <Box>
            <TextField
              label="NHS Number"
              value={searchParams.nhs_number}
              onChange={(e) => handleInputChange("nhs_number", e.target.value)}
              size="small"
            />
          </Box>

          <Box>
            <TextField
              label="Pathology Category"
              value={searchParams.pathology_category}
              onChange={(e) =>
                handleInputChange("pathology_category", e.target.value)
              }
              size="small"
            />
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <TextField
              label="Start Filing Date"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={searchParams.start_filing_date || ""}
              onChange={(e) =>
                handleInputChange("start_filing_date", e.target.value)
              }
            />

            <TextField
              label="End Filing Date"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={searchParams.end_filing_date || ""}
              onChange={(e) =>
                handleInputChange("end_filing_date", e.target.value)
              }
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<X size={18} />}
                onClick={handleClearSearch}
              >
                Clear
              </Button>

              <Button
                variant="contained"
                startIcon={<Search />}
                onClick={handleSearch}
                disabled={loadingSearch}
              >
                {loadingSearch ? "Searching..." : "Search"}
              </Button>
            </Box>
          </Box>
        </Box>

        {searchError && (
          <Box sx={{ mt: 2 }}>
            <Typography color="error" variant="body2">
              Error: {searchError}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Results Section */}
      {(loadingSearch || searchResults.length > 0) && (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
            Search Results {!loadingSearch && `(${searchResults.length})`}
          </Typography>

          {loadingSearch ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid
              rows={searchResults}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10 },
                },
              }}
              pageSizeOptions={[10, 25, 50]}
              onRowClick={handleRowClick}
              autoHeight
              sx={{
                "& .MuiDataGrid-row:hover": {
                  cursor: "pointer",
                  backgroundColor: "#f5f5f5",
                },
              }}
            />
          )}
        </Paper>
      )}

      {/* Filing Detail Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="xl"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          Filing Details
          <Button
            onClick={handleCloseDialog}
            sx={{ float: "right" }}
            color="error"
            variant="outlined"
          >
            Close
          </Button>
        </DialogTitle>

        <DialogContent dividers>
          {loadingDetail ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading filing...</Typography>
            </Box>
          ) : selectedFiling && Object.keys(selectedFiling).length > 0 ? (
            <>
              <LabFilingDetails lab={selectedFiling} />
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PathologySearch;
